/**
 * [uTheory](http://utheory.com) - Copyright (c) Gregory Ristow 2018
 * Write a given scale degree on the staff.
 */
import type { ModeName } from 'shared/definitions/musicTheory/scales';
import type { SolfegeSystemName } from 'shared/definitions/musicTheory/solfegeAndScaleDegrees';
import Clef from '../uTheory.Notation/clef';
import Staff from '../uTheory.Notation/staff';
import NoteHolder from '../uTheory.Notation/noteHolder';
import Editable from '../uTheory.Notation/editable';
import Measure from '../uTheory.Notation/measure';
import Voice from '../uTheory.Notation/voice';
import KeySignature from '../uTheory.Notation/keySignature';
import Music from '../uTheory.Notation/music';
import { makeString } from '../uTheory.Skills/skills';
import type { InteractiveOptions, UserData } from './interactivePrototype';
import InteractivePrototype from './interactivePrototype';
import type { AnswerAndData } from './interactiveQuestionPrototype';
import type { ScaleDegreeNoteIDQuestion } from './scaleDegreeNoteQuestionGenerator';
import {
  generateScaleDegreeIdentifyTonicQuestions,
  generateScaleDegreeNoteQuestions,
  isIdentifyTonicSkill,
  isLabelingSolfegeSkill,
  isWritingSolfegeSkill,
  processManualScaleDegreeIdentifyTonicQuestions,
  processManualScaleDegreeNoteQuestions,
  scaleDegreeNoteSkillsToAvailableModes,
} from './scaleDegreeNoteQuestionGenerator';
import type { SolfegePresentationSystem } from './solfegeDefinitions';
import { Resolution } from '../uTheory.Notation/resolution';
import type { InteractiveRegistryEntry } from '../uTheory.Skills/interactiveRegistry';

export interface ScaleDegreeToStaffOptions extends InteractiveOptions {
  /** Whether numbers (1, 2, #7, etc.) or names (tonic, supertonic, leading tone, etc.) is used */
  presentationSystem?: SolfegePresentationSystem;
  /** Changes key after this number of question */
  changeKeyEvery?: number;
  // /** What mode are we in? */
  // mode?: ModeName | 'allMinors';
  identifyTonicMode?: boolean;
}

export default class ScaleDegreeToStaff extends InteractivePrototype {
  declare options: ScaleDegreeToStaffOptions;

  declare currentQuestion: ScaleDegreeNoteIDQuestion;

  declare questions: ScaleDegreeNoteIDQuestion[];

  private availableModes: (ModeName | 'allMinors')[] = [];

  private solfegeSystem: SolfegeSystemName;

  private noteHolder: NoteHolder;

  constructor(displayDiv: HTMLDivElement, options: ScaleDegreeToStaffOptions, userData: UserData) {
    options = { ...options };
    super(displayDiv, options, userData);
    // Place custom settings & Layout information here:
    this.settings = {
      instructions: true,
      editor: {
        fixedWidth: 150,
      },
      letters: {
        letterButtons: false,
        accidentalButtons: { noDoubleTap: true },
        checkButton: 'Check',
      },
      checkMark: {},
      player: {},
      progressTracker: true,
    };

    this.layout = {
      editor: { percent: 80 },
      instructions: { maxPercent: 30 },
      letters: {
        percent: 20,
        visible: true,
      },
    };

    this.targetSpeedFromSkills = [
      'labeling_solfege_major',
      'labeling_solfege_natural_minor',
      'labeling_solfege_all_minors',
      'scale_degree_conversion_major',
      'scale_degree_conversion_minor',
    ];

    try {
      this.init(displayDiv, options, userData);
    } catch (err) {
      this.loadError(err);
    }
  }

  init(displayDiv: HTMLDivElement, options: ScaleDegreeToStaffOptions, userData: UserData) {
    options.maxQuestions = options.maxQuestions || 10;

    if (options.drill && !options.skills) {
      throw Error('scaleDegreeToStaff requires skills array when run in drill mode.');
    }

    if (options.drill && options.skills && !options.identifyTonicMode) {
      const skills = options.skills.map(makeString);
      this.availableModes = scaleDegreeNoteSkillsToAvailableModes(skills);

      if (!this.availableModes.length) {
        throw Error(
          "scaleDegreeToStaff skills did not map onto available modes. Maybe there's a typo in the skills list?"
        );
      }
    }

    // Set whether this interactive uses degree numbers or names
    if (!options.presentationSystem) {
      const useNames = options.skills?.includes('scale_degree_names');

      // We use the student's solfege system if we're training a solfege skill
      const useStudentSolfegeSystem = !!options.skills
        ?.map((skill) => makeString(skill))
        .filter((t): t is string => !!t)
        .find((skill) => isLabelingSolfegeSkill(skill) || isWritingSolfegeSkill(skill));
      const studentSolfegeSystem = userData?.userStore.preferences.solfegeMethod || 'scaleDegrees';

      if (useNames) options.presentationSystem = 'names';
      else if (useStudentSolfegeSystem) {
        options.presentationSystem =
          studentSolfegeSystem === 'scaleDegrees' ? 'scaleDegrees' : 'solfege';
      } else {
        options.presentationSystem = 'scaleDegrees';
      }
    }

    this.solfegeSystem = userData?.userStore.preferences.solfegeMethod || 'solfegeDoMinor';

    this.questionPrompt = (questionTexts: string[]) => {
      let prompt = `Write ${options.presentationSystem === 'names' ? 'the ' : ''}${
        questionTexts[0]
      } in ${this.currentQuestion.key.quality}.`;
      if (!options.testMode) {
        prompt += `<br />#${questionTexts[2]} of ${this.questions.length}`;
      }
      return prompt;
    };

    return super.init(displayDiv, options, userData);
  }

  initializeMusic() {
    const clef = new Clef();
    const staff = new Staff(clef);
    this.noteHolder = new NoteHolder('', 'w');
    this.noteHolder.editable = new Editable(this.noteHolder, {
      pitch: true,
      accidental: false,
      deleteable: false,
      selectable: true,
      highestLine: 8,
      lowestLine: -2,
    });
    const measure = new Measure(this.noteHolder);

    const voice = new Voice(measure);
    voice.setStaff(staff);

    const keySig = new KeySignature('C', 'Major');
    this.music = new Music(staff, voice, keySig);
  }

  generateQuestions(questions: string[] = this.options.questions): ScaleDegreeNoteIDQuestion[] {
    const { presentationSystem } = this.options;

    if (this.options.identifyTonicMode) {
      if (this.options.drill) {
        const skills = this.options.skills?.map(makeString).filter(isIdentifyTonicSkill);
        if (!skills || !skills.length) {
          throw Error(
            'options.skills must include a valid identifyTonicSkill for scaleDegreeToStaff in identifyTonic drill mode.'
          );
        }
        this.questions = generateScaleDegreeIdentifyTonicQuestions(
          this.options.maxQuestions,
          skills,
          presentationSystem,
          this.solfegeSystem
        );
      } else {
        if (!this.options.questions) throw Error('Questions are required when not in drill mode.');
        this.questions = processManualScaleDegreeIdentifyTonicQuestions(
          questions,
          presentationSystem,
          this.solfegeSystem
        );
      }
    } else {
      this.questions = this.options.drill
        ? generateScaleDegreeNoteQuestions(
            this.options.maxQuestions,
            this.availableModes,
            presentationSystem,
            this.solfegeSystem,
            this.options.changeKeyEvery,
            'write'
          )
        : processManualScaleDegreeNoteQuestions(
            questions,
            presentationSystem,
            this.solfegeSystem,
            'write'
          );
    }

    return this.questions;
  }

  addListeners() {
    this.letterPanel.addAccidentalCallback((accidental) => this.editor.setAccidental(accidental));
    this.letterPanel.addCheckCallback(() => this.checkAnswer());
  }

  newQuestion() {
    super.newQuestion();
    const accidentalButtonVisibility =
      this.currentQuestion.mode !== 'Major' && this.currentQuestion.mode !== 'naturalMinor';
    document.querySelectorAll('.uLetterPanelAccidentalButton').forEach((button) => {
      (button as HTMLButtonElement).style.display = accidentalButtonVisibility
        ? 'inline-block'
        : 'none';
    });
    this.music.staves[0].setClef(this.currentQuestion.clef);
    this.music.setKeySignature(this.currentQuestion.key);
    this.noteHolder.clearNotes();
    this.noteHolder.editable.selected = false;
    this.noteHolder.editable.setKeySignature(this.currentQuestion.key);
    this.editor.renderer.render();
    this.resize();
  }

  private getAnswerInformation() {
    const expectedAnswer = this.currentQuestion.note.toShortString();
    const studentAnswer = this.noteHolder.notes[0]
      ? this.noteHolder.notes[0].toShortString()
      : '__';
    const correct = expectedAnswer === studentAnswer;
    return {
      expectedAnswer,
      studentAnswer,
      correct,
    };
  }

  checkAnswer() {
    const { correct } = this.getAnswerInformation();
    if (this.options.testMode) {
      return this.checkpointAnswer(correct);
    }

    // When correct, call rightAnswer();
    if (correct) return this.rightAnswer(2000);
    // When incorrect, call wrongAnswer();
    return this.wrongAnswer();
  }

  playAnswer() {
    const resolver = new Resolution(this.player);
    const noteToPlay = this.noteHolder.notes[0] || this.currentQuestion.note;

    resolver.resolve(this.currentQuestion.key, noteToPlay, { tempo: 100 });
  }

  // ## CheckPoint Mode Questions:
  newCheckpointQuestion(questionString) {
    return this.loadingPromise.then(() => {
      this.generateQuestions([questionString]);
      this.questionNumber = 0;
      return Promise.resolve(this.newQuestion());
    });
  }

  // For checkpoint tests, the answer & data from the current question is
  // logged via a callback.
  getAnswerAndData(): AnswerAndData {
    const { correct, expectedAnswer, studentAnswer } = this.getAnswerInformation();
    const questionText = `Write ${this.options.presentationSystem === 'names' ? 'the ' : ''}${
      this.currentQuestion.text[0]
    } in ${this.currentQuestion.key.quality}.`;

    const answerAndData = {
      question: `${this.currentQuestion.key}: ${this.currentQuestion.degree}`,
      questionType: 'ScaleDegreeToStaff',
      questionText,
      questionSVG: null,
      studentAnswerText: studentAnswer,
      studentAnswerSVG: this.editor.exportSVG(),
      correctAnswerText: expectedAnswer,
      rightAnswers: correct ? 1 : 0,
      wrongAnswers: correct ? 0 : 1,
      skills: this.currentQuestion.skills,
    };

    return answerAndData;
  }

  static registry: InteractiveRegistryEntry<ScaleDegreeToStaffOptions>[] = [
    {
      description: 'Write scale degrees/solfege on the staff (Major)',
      skills: ['writing_solfege_major'],
    },
    {
      description: 'Write scale degrees/solfege on the staff (Natural minor)',
      skills: ['writing_solfege_natural_minor'],
    },
    {
      description: 'Write scale degrees/solfege on the staff (All minors)',
      skills: ['writing_solfege_all_minors'],
    },
    {
      description: 'Identify tonic (Major)',
      skills: [
        'identify_tonic_c_major',
        'identify_tonic_major_sharp_keys',
        'identify_tonic_major_flat_keys',
      ],
      options: {
        identifyTonicMode: true,
      },
    },
    {
      description: 'Identify tonic (minor)',
      skills: [
        'identify_tonic_a_minor',
        'identify_tonic_minor_sharp_keys',
        'identify_tonic_minor_flat_keys',
      ],
      options: {
        identifyTonicMode: true,
      },
    },
  ];
}
